import React, { useContext, useEffect, useMemo, useState } from "react";

import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";

import TradesTable from "../table/TradesTable";
import PositionsTable from "../table/PositionsTable";
import { useTheme } from "@mui/material/styles";
import { PositionsDataContext } from "../../context/PositionsDataContext";
import { getOtherStrategiesPnlTableColumns } from "../table/columnsConfig/pnlTable";
import { CardWrapper, TabNavigation } from "../ui";
import { Box, useMediaQuery } from "@mui/material";
import {
  getCommonBodyCellProps,
  getCommonHeadCellProps,
} from "../table/columnsConfig/commonUtils";

const OtherStrategies_PNL = () => {
  const theme = useTheme();
  const { datasets } = useContext(PositionsDataContext);
  const [allData, setAllData] = useState([]);
  const [maPositions, setMaPositions] = useState([]);
  const [tradedData, setTradedData] = useState([]);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [otherStrategies_activeTab, setOtherStrategiesActiveTab] =
    useState("tab1");

  // Update filter name
  const [positionsFilter, setPositionsFilter] = useState(
    () => localStorage.getItem("otherStrategiesPnlFilter") || "",
  );

  // Add state for expanded rows
  const [expandedPnLRows, setExpandedPnLRows] = useState(() => {
    const saved = localStorage.getItem("otherStrategiesPnLTableExpandedRows");
    return saved ? JSON.parse(saved) : {};
  });

  // Add state for trades table filter, sorting, and scroll position (match Home.js logic)
  const [otherTradesGlobalFilter, setOtherTradesGlobalFilter] = useState(
    () => localStorage.getItem("otherStrategiesTradesTableGlobalFilter") || "",
  );
  const [otherTradesSorting, setOtherTradesSorting] = useState(() => {
    const savedSorting = localStorage.getItem(
      "otherStrategiesTradesTableSorting",
    );
    return savedSorting
      ? JSON.parse(savedSorting)
      : [{ id: "StrategyName", desc: false }];
  });
  const [otherTradesScrollPosition, setOtherTradesScrollPosition] = useState(
    () => {
      const saved = localStorage.getItem(
        "otherStrategiesTradesTableScrollPosition",
      );
      return saved ? parseInt(saved, 10) : 0;
    },
  );

  // Add state for positions table filter, sorting, and scroll position (match Home.js logic)
  const [otherPositionsGlobalFilter, setOtherPositionsGlobalFilter] = useState(
    () =>
      localStorage.getItem("otherStrategiesPositionsTableGlobalFilter") || "",
  );
  const [otherPositionsSorting, setOtherPositionsSorting] = useState(() => {
    const savedSorting = localStorage.getItem(
      "otherStrategiesPositionsTableSorting",
    );
    return savedSorting
      ? JSON.parse(savedSorting)
      : [{ id: "StrategyName", desc: false }];
  });
  const [otherPositionsScrollPosition, setOtherPositionsScrollPosition] =
    useState(() => {
      return (
        parseInt(
          localStorage.getItem("otherStrategiesPositionsTableScrollPosition"),
          10,
        ) || 0
      );
    });

  const handleTabClick = (tab) => {
    setOtherStrategiesActiveTab(tab);
    localStorage.setItem("otherStrategies_activeTab", tab); // Save active tab to localStorage
  };

  // Effects
  useEffect(() => {
    if (datasets?.positions) setAllData(datasets.positions);
  }, [datasets]);

  // Update filtering logic to exclude all specified strategies
  useEffect(() => {
    if (allData?.length > 0) {
      // Filter for other strategies
      const otherPositions = allData?.filter(
        (position) =>
          ![
            "Merger Arbitrage",
            "SPAC",
            "Convertible Arbitrage",
            "Called or Tendered",
            "Expected Call",
            "RED",
            "Yield to Maturity",
            "Credit Arbitrage",
            "Stat Arbitrage",
            "Vol Arbitrage",
          ].includes(position.StrategyType),
      );
      setMaPositions(otherPositions);

      // Filter for positions with trades
      const positionsWithTrades = allData?.filter(
        (position) =>
          (position.buy_trade_quantity ||
            position.sell_trade_quantity ||
            position.short_trade_quantity ||
            position.cover_trade_quantity) &&
          ![
            "Merger Arbitrage",
            "SPAC",
            "Convertible Arbitrage",
            "Called or Tendered",
            "Expected Call",
            "RED",
            "Yield to Maturity",
            "Credit Arbitrage",
            "Stat Arbitrage",
            "Vol Arbitrage",
          ].includes(position.StrategyType),
      );
      setTradedData(positionsWithTrades);
    }
  }, [allData]);

  // Update filtering logic in useMemo
  const filteredPositions = useMemo(() => {
    return allData?.filter(
      (position) =>
        ![
          "Merger Arbitrage",
          "SPAC",
          "Convertible Arbitrage",
          "Called or Tendered",
          "Expected Call",
          "RED",
          "Yield to Maturity",
          "Credit Arbitrage",
          "Stat Arbitrage",
          "Vol Arbitrage",
        ].includes(position.StrategyType) &&
        (positionsFilter === "" ||
          ["StrategyName", "security_name", "SYMBOL"].some((key) =>
            position[key]
              ?.toString()
              .toLowerCase()
              .includes(positionsFilter.toLowerCase()),
          )),
    );
  }, [allData, positionsFilter]);

  // Update the checkAndClearStorageIfNewDay function
  const checkAndClearStorageIfNewDay = () => {
    const lastAccessDate = localStorage.getItem(
      "otherStrategiesPnLLastAccessDate",
    );
    const currentDate = new Date().toDateString();

    if (lastAccessDate !== currentDate) {
      // Clear all OtherStrategies PNL related localStorage items
      localStorage.removeItem("otherStrategiesPnLTableExpandedRows");
      localStorage.removeItem("otherStrategiesPnlFilter");
      localStorage.removeItem("otherStrategies_activeTab");
      localStorage.removeItem("otherStrategiesPnLTableGlobalFilter");
      localStorage.removeItem("otherStrategiesPositionsTableGlobalFilter");
      localStorage.removeItem("otherStrategiesPositionsTableSorting");
      localStorage.removeItem("otherStrategiesPositionsTableScrollPosition");

      // Update the last access date
      localStorage.setItem("otherStrategiesPnLLastAccessDate", currentDate);

      // Reset the expanded state
      setExpandedPnLRows({});
    }
  };

  useEffect(() => {
    localStorage.setItem("positionsFilter", positionsFilter);
  }, [positionsFilter]);

  // Add the highlightMatch function
  const highlightMatch = (text, filter) => {
    if (!filter || !text) return text;
    const regex = new RegExp(`(${filter})`, "gi");
    return text.split(regex)?.map((part, i) =>
      regex.test(part) ? (
        <span key={i} style={{ backgroundColor: "yellow" }}>
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  // Add useEffect for sorting persistence
  useEffect(() => {
    localStorage.setItem(
      "otherStrategiesPositionsTableSorting",
      JSON.stringify(otherPositionsSorting),
    );
  }, [otherPositionsSorting]);

  // Add useEffect for scroll position persistence
  useEffect(() => {
    const container = document.querySelector(
      '[class*="MuiTableContainer-root"]',
    );
    if (container) {
      container.addEventListener("scroll", () => {
        setOtherPositionsScrollPosition(container.scrollTop);
        localStorage.setItem(
          "otherStrategiesPositionsTableScrollPosition",
          container.scrollTop,
        );
      });
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", () => {});
      }
    };
  }, []);

  // Add useEffect for global filter persistence
  useEffect(() => {
    localStorage.setItem(
      "otherStrategiesPositionsTableGlobalFilter",
      otherPositionsGlobalFilter || "",
    );
  }, [otherPositionsGlobalFilter]);

  // Add useEffect to restore global filter on component mount
  useEffect(() => {
    const savedFilter = localStorage.getItem(
      "otherStrategiesPositionsTableGlobalFilter",
    );
    if (savedFilter) {
      setOtherPositionsGlobalFilter(savedFilter);
    }
  }, []);

  // Save filter, sorting, and scroll position to localStorage
  useEffect(() => {
    localStorage.setItem(
      "otherStrategiesTradesTableGlobalFilter",
      otherTradesGlobalFilter || "",
    );
  }, [otherTradesGlobalFilter]);
  useEffect(() => {
    localStorage.setItem(
      "otherStrategiesTradesTableSorting",
      JSON.stringify(otherTradesSorting),
    );
  }, [otherTradesSorting]);
  useEffect(() => {
    localStorage.setItem(
      "otherStrategiesTradesTableScrollPosition",
      otherTradesScrollPosition,
    );
  }, [otherTradesScrollPosition]);

  // Restore scroll position on tab switch and refresh
  useEffect(() => {
    if (otherStrategies_activeTab === "tab2") {
      setTimeout(() => {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]',
        );
        if (container) {
          const savedScroll =
            parseInt(
              localStorage.getItem("otherStrategiesTradesTableScrollPosition"),
              10,
            ) || 0;
          container.scrollTop = savedScroll;
        }
      }, 200);
    }
  }, [otherStrategies_activeTab]);

  // Save scroll position on scroll
  useEffect(() => {
    if (otherStrategies_activeTab === "tab2") {
      const container = document.querySelector(
        '[class*="MuiTableContainer-root"]',
      );
      if (container) {
        const handleScroll = () => {
          setOtherTradesScrollPosition(container.scrollTop);
        };
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
      }
    }
  }, [otherStrategies_activeTab]);

  // Update the pnl_table configuration
  const pnl_table = useMaterialReactTable({
    columns: getOtherStrategiesPnlTableColumns(theme),
    data: maPositions,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    initialState: {
      grouping: ["StrategyName"],
      sorting: [{ id: "total_pnl", desc: true }],
      density: "compact",
      expanded: expandedPnLRows,
      columnPinning: isDesktop
        ? { left: ["StrategyName"] } // Pin StrategyName on desktop
        : {}, // No columns pinned in mobile mode
    },
    state: {
      expanded: expandedPnLRows,
    },
    onExpandedChange: (updater) => {
      const newExpanded =
        typeof updater === "function" ? updater(expandedPnLRows) : updater;
      setExpandedPnLRows(newExpanded);
      localStorage.setItem(
        "mabPnLTableExpandedRows",
        JSON.stringify(newExpanded),
      );
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row }) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${row.depth * 2}rem`,
              cursor: "pointer",
            }}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <RemoveIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} /> // Responsive icon size
            ) : (
              <AddIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} />
            )}
            {row.getIsGrouped() && (
              <span
                style={{
                  marginLeft: "8px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: { xs: "0.75rem", sm: "0.75rem" }, // Responsive font size
                }}
              >
                {row.groupingValue}
              </span>
            )}
          </div>
        ),
      },
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 130px)", // Default for desktop
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });

  // Tab configuration
  const tabs = [
    { value: "tab1", label: "Other Strategies P&L" },
    { value: "tab2", label: "Other Strategies Trades Today" },
    { value: "tab3", label: "Other Strategies Positions" },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {/* Tab Navigation */}
      <CardWrapper sx={{ mb: 3 }}>
        <TabNavigation
          tabs={tabs}
          activeTab={otherStrategies_activeTab}
          onTabChange={handleTabClick}
        />
      </CardWrapper>

      {/* Tab content */}
      <Box sx={{ backgroundColor: theme.palette.background.default }}>
        {otherStrategies_activeTab === "tab1" && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 0,
              boxShadow: theme.shadows[1],
            }}
          >
            <MaterialReactTable table={pnl_table} />
          </Box>
        )}
        {otherStrategies_activeTab === "tab2" && (
          <TradesTable
            data={tradedData}
            title="Other Strategies Trades Today"
            globalFilter={otherTradesGlobalFilter}
            setGlobalFilter={setOtherTradesGlobalFilter}
            sorting={otherTradesSorting}
            setSorting={setOtherTradesSorting}
            scrollPosition={otherTradesScrollPosition}
          />
        )}
        {otherStrategies_activeTab === "tab3" && (
          <PositionsTable
            data={maPositions}
            title="Other Strategies Positions"
            globalFilter={otherPositionsGlobalFilter}
            setGlobalFilter={setOtherPositionsGlobalFilter}
            sorting={otherPositionsSorting}
            setSorting={setOtherPositionsSorting}
            scrollPosition={otherPositionsScrollPosition}
          />
        )}
      </Box>
    </Box>
  );
};

export default OtherStrategies_PNL;
